/**
 * OfflineQueueService - Platform-agnostic offline queue management
 * 
 * This service is:
 * - React-agnostic (no hooks, no useState)
 * - Portable to React Native
 * 
 * Handles queueing of expense operations when offline and syncing when online.
 */

import { supabase } from '@/integrations/supabase/client';
import { storageService, storageHelpers, STORAGE_KEYS } from './storageService';

export type QueuedExpense = {
  id: string;
  action: 'insert' | 'update' | 'delete';
  data: any;
  timestamp: number;
};

class OfflineQueueService {
  private queue: QueuedExpense[] = [];
  private isSyncing: boolean = false;
  private listeners: Set<(queue: QueuedExpense[]) => void> = new Set();

  /**
   * Initialize the service - load queue from storage
   */
  async initialize(): Promise<void> {
    this.queue = await storageHelpers.getJSON<QueuedExpense[]>(
      STORAGE_KEYS.EXPENSE_QUEUE,
      []
    );
  }

  /**
   * Get current queue
   */
  getQueue(): QueuedExpense[] {
    return [...this.queue];
  }

  /**
   * Get pending count
   */
  getPendingCount(): number {
    return this.queue.length;
  }

  /**
   * Check if currently syncing
   */
  getIsSyncing(): boolean {
    return this.isSyncing;
  }

  /**
   * Add item to queue
   */
  async enqueue(action: 'insert' | 'update' | 'delete', data: any): Promise<void> {
    const item: QueuedExpense = {
      id: crypto.randomUUID(),
      action,
      data,
      timestamp: Date.now(),
    };
    
    this.queue.push(item);
    await this.saveQueue();
    this.notifyListeners();
  }

  /**
   * Sync queue with backend
   */
  async syncQueue(): Promise<void> {
    if (this.queue.length === 0 || this.isSyncing) return;

    this.isSyncing = true;
    this.notifyListeners();

    const failedItems: QueuedExpense[] = [];

    for (const item of this.queue) {
      try {
        if (item.action === 'insert') {
          await supabase.from('expenses').insert(item.data);
        } else if (item.action === 'update') {
          await supabase.from('expenses').update(item.data).eq('id', item.data.id);
        } else if (item.action === 'delete') {
          await supabase.from('expenses').delete().eq('id', item.data.id);
        }
      } catch (error) {
        console.error('OfflineQueueService sync error:', error);
        failedItems.push(item);
      }
    }

    this.queue = failedItems;
    this.isSyncing = false;
    await this.saveQueue();
    this.notifyListeners();
  }

  /**
   * Subscribe to queue changes
   */
  subscribe(listener: (queue: QueuedExpense[]) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Private: Save queue to storage
   */
  private async saveQueue(): Promise<void> {
    await storageHelpers.setJSON(STORAGE_KEYS.EXPENSE_QUEUE, this.queue);
  }

  /**
   * Private: Notify all listeners
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.queue));
  }
}

// Singleton instance
export const offlineQueueService = new OfflineQueueService();
